import { formatSlot, utcToZoned, zonedToUtc, type PollAnswer, type PollDTO } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Ballot } from '../components/Ballot';
import { useCampaign, useCampaignId, useInvalidateCampaign } from '../hooks';
import { t, tDyn } from '../i18n';
import { classNames } from '../util';
import { StatusBadge } from './PollsPage';

/** Matrix-Zelle: leer → Ja → Vielleicht → Nein → leer. */
const cycle = (a: PollAnswer | undefined): PollAnswer | null => (a === undefined ? 'yes' : a === 'yes' ? 'maybe' : a === 'maybe' ? 'no' : null);
const SYMBOL: Record<PollAnswer, string> = { yes: '✓', maybe: '~', no: '✗' };

export function PollPage() {
  const cid = useCampaignId();
  const { pid = '' } = useParams();
  const campaign = useCampaign(cid);
  if (!campaign.data) return <div className="muted pad">{t('common.loading')}</div>;
  return campaign.data.role === 'player' ? <PlayerPoll cid={cid} pid={pid} /> : <PollAdmin cid={cid} pid={pid} />;
}

function BackLink({ cid }: { cid: string }) {
  return (
    <Link to={`/c/${cid}/polls`} className="small">
      ← {t('polls.back')}
    </Link>
  );
}

function PlayerPoll({ cid, pid }: { cid: string; pid: string }) {
  const qc = useQueryClient();
  const ballot = useQuery({ queryKey: ['ballot', cid, pid], queryFn: () => api.ballot(cid, pid) });
  const schedule = useQuery({ queryKey: ['schedule', cid], queryFn: () => api.schedule(cid) });
  const noteId = schedule.data?.next?.pollId === pid ? schedule.data.next.sessionNoteId : null;
  if (ballot.isError) return <div className="page-narrow error">{(ballot.error as Error).message}</div>;
  if (!ballot.data) return <div className="muted pad">{t('common.loading')}</div>;
  return (
    <div className="page-narrow polls-page">
      <BackLink cid={cid} />
      <h1>{ballot.data.title}</h1>
      <Ballot
        ballot={ballot.data}
        icsUrl={ballot.data.final ? api.pollIcsUrl(cid, pid) : undefined}
        sessionLink={noteId && <Link to={`/c/${cid}/n/${noteId}`}>{t('polls.openSession')}</Link>}
        onVote={async (slotId, answer, comment) => {
          const res = await api.castBallot(cid, pid, slotId, answer, comment);
          qc.setQueryData(['ballot', cid, pid], res);
          void qc.invalidateQueries({ queryKey: ['schedule', cid] });
          return res;
        }}
      />
    </div>
  );
}

function PollAdmin({ cid, pid }: { cid: string; pid: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const invalidateCampaign = useInvalidateCampaign(cid);
  const poll = useQuery({ queryKey: ['poll', cid, pid], queryFn: () => api.poll(cid, pid) });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (poll.data) document.title = `${poll.data.title} · Kartothek`;
  }, [poll.data]);

  const run = async (fn: () => Promise<PollDTO | void>) => {
    setError(null);
    try {
      const res = await fn();
      if (res) qc.setQueryData(['poll', cid, pid], res);
      void qc.invalidateQueries({ queryKey: ['polls', cid] });
      void qc.invalidateQueries({ queryKey: ['schedule', cid] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  if (poll.isError) return <div className="page-narrow error">{(poll.error as Error).message}</div>;
  const p = poll.data;
  if (!p) return <div className="muted pad">{t('common.loading')}</div>;
  const done = p.status === 'fixed' || p.status === 'cancelled';
  const final = p.slots.find((s) => s.id === p.finalSlotId);

  const finalize = (slotId: string) => {
    const s = p.slots.find((x) => x.id === slotId)!;
    if (!confirm(t('polls.finalizeConfirm', { slot: formatSlot(s.startAt, s.endAt) }))) return;
    void run(async () => {
      const res = await api.finalizePoll(cid, pid, slotId);
      invalidateCampaign();
      void qc.invalidateQueries({ queryKey: ['notesOfType', cid, 'session'] });
      return res;
    });
  };

  return (
    <div className="page-wide polls-page">
      <BackLink cid={cid} />
      <div className="page-head">
        <h1>
          {p.title} <StatusBadge p={p} />
        </h1>
        <div className="row-form">
          {p.status === 'open' && <button onClick={() => void run(() => api.updatePoll(cid, pid, { status: 'closed' }))}>{t('polls.close')}</button>}
          {p.status === 'closed' && <button onClick={() => void run(() => api.updatePoll(cid, pid, { status: 'open' }))}>{t('polls.reopen')}</button>}
          {!done && (
            <button onClick={() => confirm(t('polls.cancelConfirm')) && void run(() => api.updatePoll(cid, pid, { status: 'cancelled' }))}>
              {t('polls.cancel')}
            </button>
          )}
          <button
            className="danger"
            onClick={() =>
              confirm(t('polls.deleteConfirm', { title: p.title })) &&
              void run(async () => {
                await api.deletePoll(cid, pid);
                navigate(`/c/${cid}/polls`);
              })
            }
          >
            {t('polls.delete')}
          </button>
        </div>
      </div>
      {(p.description || p.locationHint) && (
        <p className="muted">
          {p.locationHint && (
            <>
              {t('polls.location')}: {p.locationHint}
              {p.description && ' · '}
            </>
          )}
          {p.description}
        </p>
      )}
      {final && (
        <div className="ballot-final">
          <strong>{t('polls.fixedOn', { slot: formatSlot(final.startAt, final.endAt) })}</strong>
          <div className="row-form">
            {p.sessionNoteId && (
              <Link className="button primary" to={`/c/${cid}/n/${p.sessionNoteId}`}>
                {t('polls.openSession')}
              </Link>
            )}
            <a className="button" href={api.pollIcsUrl(cid, pid)} download>
              {t('polls.ics')}
            </a>
          </div>
        </div>
      )}
      {error && <div className="error">{error}</div>}

      <Matrix p={p} done={done} onFinalize={finalize} run={run} cid={cid} />
      {!done && <AddSlot onAdd={(slot) => run(() => api.addPollSlot(cid, pid, slot))} />}
      <Participants p={p} cid={cid} done={done} run={run} />
      {!done && <Settings p={p} onSave={(input) => run(() => api.updatePoll(cid, pid, input))} />}
    </div>
  );
}

function Matrix({
  p,
  cid,
  done,
  onFinalize,
  run,
}: {
  p: PollDTO;
  cid: string;
  done: boolean;
  onFinalize: (slotId: string) => void;
  run: (fn: () => Promise<PollDTO | void>) => Promise<void>;
}) {
  const votes = useMemo(() => new Map(p.votes.map((v) => [`${v.participantId}:${v.slotId}`, v])), [p.votes]);
  const stats = useMemo(() => new Map(p.ranking.map((r) => [r.slotId, r])), [p.ranking]);
  const best = p.ranking[0] && !p.ranking[0].blocked && (p.ranking[0].quorumMet || p.ranking[0].score > 0) ? p.ranking[0].slotId : null;

  return (
    <section className="card">
      <div className="poll-matrix-wrap">
        <table className="poll-matrix">
          <thead>
            <tr>
              <th />
              {p.slots.map((s) => (
                <th key={s.id} className={classNames(s.id === best && 'best', s.id === p.finalSlotId && 'final')}>
                  <div>{formatSlot(s.startAt, s.endAt, { year: false })}</div>
                  {s.id === best && !done && <div className="small poll-best">{t('polls.best')}</div>}
                  {!done && (
                    <button
                      className="icon-btn small"
                      title={t('common.delete')}
                      onClick={() =>
                        confirm(t('polls.removeSlotConfirm', { slot: formatSlot(s.startAt, s.endAt) })) &&
                        void run(() => api.deletePollSlot(cid, p.id, s.id))
                      }
                    >
                      ×
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {p.participants.map((x) => (
              <tr key={x.id}>
                <th scope="row">
                  {x.displayName}
                  {x.isRequired && <span className="poll-required" title={t('polls.requiredHint')}> *</span>}
                </th>
                {p.slots.map((s) => {
                  const v = votes.get(`${x.id}:${s.id}`);
                  return (
                    <td key={s.id} className={classNames('poll-cell', v && `poll-${v.answer}`)} title={v?.comment || undefined}>
                      <button
                        disabled={done}
                        aria-label={`${x.displayName}, ${formatSlot(s.startAt)}: ${tDyn(`polls.answer.${v?.answer ?? 'none'}`)}`}
                        onClick={() => void run(() => api.setPollVote(cid, p.id, x.id, s.id, cycle(v?.answer)))}
                      >
                        {v ? SYMBOL[v.answer] : '·'}
                        {v?.comment && <sup>💬</sup>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t('polls.sum')}</th>
              {p.slots.map((s) => {
                const r = stats.get(s.id);
                return (
                  <td key={s.id} className={classNames('poll-sum', r?.quorumMet && 'quorum', r?.blocked && 'blocked')}>
                    {r && (
                      <>
                        <div>
                          {r.yes} ✓ · {r.maybe} ~
                        </div>
                        <div className="small muted">{t('polls.points', { n: r.score })}</div>
                        {r.quorumMet && <div className="small">{t('polls.quorum')}</div>}
                        {r.blocked && <div className="small">{t('polls.blocked')}</div>}
                      </>
                    )}
                    {!done && (
                      <button className={classNames('small-btn', s.id === best && 'primary')} onClick={() => onFinalize(s.id)}>
                        {t('polls.finalize')}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      {!done && <p className="small muted">{t('polls.matrixHint')}</p>}
    </section>
  );
}

function AddSlot({ onAdd }: { onAdd: (slot: { startAt: string; endAt: string | null }) => Promise<void> }) {
  const [date, setDate] = useState('');
  const [start, setStart] = useState('19:00');
  const [end, setEnd] = useState('');
  return (
    <form
      className="row-form card"
      onSubmit={(e) => {
        e.preventDefault();
        if (!date || !start) return;
        const startAt = zonedToUtc(date, start);
        let endAt = end ? zonedToUtc(date, end) : null;
        if (endAt && endAt <= startAt) endAt = new Date(new Date(endAt).getTime() + 86_400_000).toISOString();
        void onAdd({ startAt, endAt }).then(() => setDate(''));
      }}
    >
      <label>
        {t('polls.dates')}
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </label>
      <label>
        {t('polls.startTime')}
        <input type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
      </label>
      <label>
        {t('polls.endTime')}
        <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
      </label>
      <button>+ {t('polls.addSlot')}</button>
    </form>
  );
}

function CopyLink({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="small-btn"
      onClick={() =>
        void navigator.clipboard.writeText(`${location.origin}${path}`).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        })
      }
    >
      {copied ? t('polls.copied') : t('polls.copyLink')}
    </button>
  );
}

function Participants({
  p,
  cid,
  done,
  run,
}: {
  p: PollDTO;
  cid: string;
  done: boolean;
  run: (fn: () => Promise<PollDTO | void>) => Promise<void>;
}) {
  const members = useQuery({ queryKey: ['members', cid], queryFn: () => api.members(cid) });
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const free = (members.data ?? []).filter((m) => !p.participants.some((x) => x.userId === m.userId));

  return (
    <section className="card">
      <h2>{t('polls.links')}</h2>
      <p className="small muted">{t('polls.linksHint')}</p>
      <table className="table poll-participants">
        <tbody>
          {p.participants.map((x) => (
            <tr key={x.id}>
              <td>
                {x.displayName}
                {x.userId && <span className="small muted"> · {t('polls.account')}</span>}
              </td>
              <td>
                <label className="inline small">
                  <input
                    type="checkbox"
                    checked={x.isRequired}
                    disabled={done}
                    onChange={(e) => void run(() => api.updatePollParticipant(cid, p.id, x.id, { isRequired: e.target.checked }))}
                  />
                  {t('polls.required')}
                </label>
              </td>
              <td className="num poll-link-actions">
                {x.link ? <CopyLink path={x.link} /> : <span className="small muted">{t('polls.revoked')}</span>}
                {p.status !== 'cancelled' && (
                  <button
                    className="small-btn"
                    onClick={() => confirm(t('polls.rotateConfirm', { name: x.displayName })) && void run(() => api.rotatePollLink(cid, p.id, x.id))}
                  >
                    {t('polls.rotate')}
                  </button>
                )}
                {x.link && (
                  <button className="small-btn" onClick={() => void run(() => api.revokePollLink(cid, p.id, x.id))}>
                    {t('polls.revoke')}
                  </button>
                )}
                {!done && (
                  <button
                    className="small-btn danger"
                    onClick={() =>
                      confirm(t('polls.removeParticipantConfirm', { name: x.displayName })) &&
                      void run(() => api.deletePollParticipant(cid, p.id, x.id))
                    }
                  >
                    {t('common.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!done && (
        <form
          className="row-form"
          onSubmit={(e) => {
            e.preventDefault();
            const m = free.find((x) => x.userId === userId);
            const displayName = m?.name ?? name.trim();
            if (!displayName) return;
            void run(() => api.addPollParticipant(cid, p.id, { displayName, userId: m?.userId ?? null, isRequired: false })).then(() => {
              setName('');
              setUserId('');
            });
          }}
        >
          {free.length > 0 && (
            <label>
              {t('polls.members')}
              <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">—</option>
                {free.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!userId && (
            <label>
              {t('common.name')}
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            </label>
          )}
          <button>+ {t('polls.addParticipant')}</button>
        </form>
      )}
    </section>
  );
}

function Settings({ p, onSave }: { p: PollDTO; onSave: (input: Parameters<typeof api.updatePoll>[2]) => Promise<void> }) {
  const dl = p.deadline ? utcToZoned(p.deadline) : null;
  const [title, setTitle] = useState(p.title);
  const [description, setDescription] = useState(p.description);
  const [locationHint, setLocationHint] = useState(p.locationHint);
  const [deadlineDate, setDeadlineDate] = useState(dl?.date ?? '');
  const [deadlineTime, setDeadlineTime] = useState(dl?.time ?? '23:59');
  const [minYes, setMinYes] = useState(p.minYes);
  const [maybeCounts, setMaybeCounts] = useState(p.maybeCounts);
  const [linkDays, setLinkDays] = useState(p.linkDays);

  return (
    <form
      className="card poll-create"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave({
          title,
          description,
          locationHint,
          deadline: deadlineDate ? zonedToUtc(deadlineDate, deadlineTime || '23:59') : null,
          minYes,
          maybeCounts,
          linkDays,
        });
      }}
    >
      <h2>{t('polls.settings')}</h2>
      <label>
        {t('polls.name')}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
      </label>
      <label>
        {t('polls.location')}
        <input value={locationHint} onChange={(e) => setLocationHint(e.target.value)} maxLength={200} />
      </label>
      <label>
        {t('polls.description')}
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={2} />
      </label>
      <div className="row-form">
        <label>
          {t('polls.minYes')} <span className="small">({t('polls.minYesHint')})</span>
          <input type="number" min={0} max={50} value={minYes} onChange={(e) => setMinYes(Number(e.target.value))} />
        </label>
        <label>
          {t('polls.deadline')}
          <input type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} />
        </label>
        {deadlineDate && (
          <label>
            &nbsp;
            <input type="time" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} />
          </label>
        )}
        <label>
          {t('polls.linkDays')}
          <input type="number" min={0} max={90} value={linkDays} onChange={(e) => setLinkDays(Number(e.target.value))} />
        </label>
      </div>
      <label className="inline">
        <input type="checkbox" checked={maybeCounts} onChange={(e) => setMaybeCounts(e.target.checked)} />
        {t('polls.maybeCounts')}
      </label>
      <div className="modal-actions">
        <button className="primary">{t('common.save')}</button>
      </div>
    </form>
  );
}
